import { useEffect } from 'react';
import { ArrowRightIcon, ClockIcon, ExternalLinkIcon } from 'lucide-react';
import { images, interiorHeroes, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
import { LeadTimeNotice } from '../components/LeadTimeNotice';
const hero = interiorHeroes.backyardLiving;
const TITLE = 'Outdoor Furniture in Brooklyn, MI | Pegasus Spas';
const DESCRIPTION = 'Explore outdoor furniture and backyard-living products at Pegasus Spas in Brooklyn, Michigan, including Polywood, Winston, hammocks, fire tables and grills.';
const CANONICAL = 'https://www.pegasus-spas.com/furniture.html';
const secondaryLines = [{
  name: 'Hatteras Hammocks',
  copy: 'Hammocks, swings and easygoing relaxation seating.',
  href: 'https://hatterashammocks.com/'
}, {
  name: 'Poly Furniture USA',
  copy: 'Additional poly-furniture styles for outdoor seating and dining.',
  href: 'https://polyfurnitureusa.com/'
}];
const categories = [{
  title: 'Big Green Egg Grills',
  copy: 'Ceramic cookers and accessories for grilling, smoking and baking outdoors.',
  href: routes.grills,
  image: interiorHeroes.bigGreenEgg.src,
  alt: interiorHeroes.bigGreenEgg.alt,
  position: '65% 55%'
}, {
  title: 'Fire Tables',
  copy: 'Warmth and light that extend evenings in the backyard.',
  href: routes.fireTables,
  image: interiorHeroes.fireTables.src,
  alt: interiorHeroes.fireTables.alt,
  position: '72% 62%'
}, {
  title: 'Patio Umbrellas',
  copy: 'Shade options for dining areas, seating groups and spa-side spaces.',
  href: routes.umbrellas,
  image: interiorHeroes.umbrellas.src,
  alt: interiorHeroes.umbrellas.alt,
  position: '70% 45%'
}, {
  title: 'Spa Steps & Access',
  copy: 'Steps and access pieces that make getting in and out of the spa easier.',
  href: routes.spaSteps,
  image: interiorHeroes.spaSteps.src,
  alt: interiorHeroes.spaSteps.alt,
  position: '62% 68%'
}];
const relatedLinks = [{
  label: 'Explore Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Current Specials',
  href: routes.specials
}, {
  label: 'Cold Plunge Tubs',
  href: routes.coldPlunge
}, {
  label: 'Shop Online',
  href: routes.supplies
}, {
  label: 'Financing',
  href: routes.financing
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function BackyardLivingPage() {
  useEffect(() => {
    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target) target.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-635" data-mp="24k6xf4f8lz2r">
      {/* Hero */}
      <section aria-labelledby="backyard-h1" className="relative w-full" data-id="element-636" data-mp="24nkzebjzo69o">
        <div className={`relative w-full overflow-hidden ${hero.frameClass}`} data-id="element-637" data-mp="1zlvocvwrnsll">
          <img src={hero.src} alt={hero.alt} className={hero.imageClass} width={hero.width} height={hero.height} decoding="async" data-id="element-638" data-mp="1vlv9xb9o3hej" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-639" data-mp="1lvihkwvm2jxu" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-640" data-mp="1gx8joeii558j" />

          <div className={`relative mx-auto flex max-w-container flex-col justify-center px-5 py-10 sm:px-8 ${hero.contentClass}`} data-id="element-641" data-mp="1bvj8myva4rkg">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-642" data-mp="1wlvhl287dbxf">
              Backyard Living
            </p>
            <h1 id="backyard-h1" className="mt-3 max-w-3xl font-display text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-643" data-mp="21jx11o9jjd9f">
              Outdoor Furniture for Michigan Backyards
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-644" data-mp="1dvdt59uel2hv">
              Create a comfortable place for coffee, cookouts, family time and slow evenings
              outside, with furniture and backyard essentials you can compare in person at Pegasus.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-645" data-mp="14x27zncb2wwt">
              <a href="#furniture-lines" className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-646" data-mp="18wt2o24ydx6m">
                Browse Outdoor Furniture
              </a>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-647" data-mp="rkm6vroc3pdo">
                Visit the Brooklyn Showroom
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Customer-approved opening */}
      <section aria-labelledby="starts-here-heading" className="w-full bg-white" data-id="element-648" data-mp="16bczh1xr2shu">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-649" data-mp="1kal3c84uyosd">
          <h2 id="starts-here-heading" className="max-w-3xl font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-650" data-mp="1fc1fu3am94xw">
            Outdoor Living Starts Here.
          </h2>
          <p className="mt-5 max-w-3xl text-body text-ink/80 sm:text-body-lg" data-id="element-651" data-mp="3h4zc693lqfnl">
            Whether you&rsquo;re enjoying a quiet morning with coffee by the lake, grilling for
            family and friends, hosting your son&rsquo;s baseball team or relaxing in the hot tub
            after a long day, the right outdoor furniture transforms your backyard into your
            favorite place to be. At Pegasus Spas, we&rsquo;ve carefully selected premium outdoor
            furniture that&rsquo;s built to withstand Michigan&rsquo;s seasons while providing years
            of comfort and style.
          </p>
        </div>
      </section>

      {/* Start with how you gather */}
      <section aria-labelledby="gather-heading" className="w-full bg-water" data-id="element-652" data-mp="2dp6x6asxuqt5">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-653" data-mp="2iojrthq1x26a">
          <h2 id="gather-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-654" data-mp="2iqmlqcubrfga">
            Start With How You Gather
          </h2>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8" data-id="element-655" data-mp="2rmvg3d3g3nvk">
            {/* Lead pathway */}
            <div className="lg:col-span-7" data-id="element-656" data-mp="b31lnq67ojo5">
              <a href="#furniture-lines" className="group block h-full rounded-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pegasus-magenta focus-visible:ring-offset-2" data-id="element-657" data-mp="b205x8e93903">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-card sm:aspect-[3/2] lg:aspect-[4/3]" data-id="element-658" data-mp="yscmt8rh4g2t">
                  <img src={images.pathBackyard} alt="Outdoor sofa, chairs and a fire table arranged on a lakeside Michigan patio" className="h-full w-full object-cover object-center transition-opacity duration-150 ease-out group-hover:opacity-95" loading="lazy" decoding="async" data-id="element-659" data-mp="uq91ztiwzz4v" />
                </div>
                <h3 className="mt-4 font-display text-[24px] font-semibold leading-8 text-ink group-hover:text-pegasus-blue sm:text-[27px]" data-id="element-660" data-mp="1dolkw6zmondn">
                  Conversation &amp; Lounge Seating
                </h3>
                <p className="mt-2 max-w-xl text-body leading-7 text-ink/75" data-id="element-661" data-mp="qud2qc7aah11">
                  A comfortable place to settle in, pass coffee around or stay outside after dinner.
                </p>
                <span className="mt-2.5 inline-flex items-center gap-1.5 text-[16px] font-bold text-pegasus-blue group-hover:text-pegasus-magenta" data-id="element-662" data-mp="h3ie516kf5cf">
                  Explore
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-663" data-mp="19rwazdlx3vme" />
                </span>
              </a>
            </div>

            {/* Stacked secondary pathways */}
            <div className="flex flex-col gap-6 lg:col-span-5 lg:gap-8" data-id="element-664" data-mp="zt6xdx120xby">
              <a href="#complete-the-backyard" className="group block rounded-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pegasus-magenta focus-visible:ring-offset-2" data-id="element-665" data-mp="zvb7wxtgkul7">
                <div className="aspect-[16/9] w-full overflow-hidden rounded-card" data-id="element-666" data-mp="3cz567j2og475">
                  <img src={images.backyardLiving} alt="Family gathered around outdoor furniture, a grill and a fire table on a backyard deck" className="h-full w-full object-cover object-[50%_65%] transition-opacity duration-150 ease-out group-hover:opacity-95" loading="lazy" decoding="async" data-id="element-667" data-mp="381lizto04h32" />
                </div>
                <h3 className="mt-3 font-display text-[21px] font-semibold leading-7 text-ink group-hover:text-pegasus-blue" data-id="element-668" data-mp="2pc5g4zougcjh">
                  Dining &amp; Entertaining
                </h3>
                <p className="mt-1.5 text-[16.5px] leading-7 text-ink/75" data-id="element-669" data-mp="2z8032703ots2">
                  Room for weeknight meals, weekend cookouts and a few extra people when the whole
                  group shows up.
                </p>
              </a>

              <SiteLink to={routes.umbrellas} className="group block rounded-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pegasus-magenta focus-visible:ring-offset-2" data-id="element-670" data-mp="1ul0ozmap1bzt">
                <div className="aspect-[16/9] w-full overflow-hidden rounded-card" data-id="element-671" data-mp="1lj17ksvro5wc">
                  <img src={interiorHeroes.umbrellas.src} alt={interiorHeroes.umbrellas.alt} className="h-full w-full object-cover object-[64%_42%] transition-opacity duration-150 ease-out group-hover:opacity-95" loading="lazy" decoding="async" data-id="element-672" data-mp="1gm150ry4ll9z" />
                </div>
                <h3 className="mt-3 font-display text-[21px] font-semibold leading-7 text-ink group-hover:text-pegasus-blue" data-id="element-673" data-mp="xv9vnqe51uvl">
                  Shade &amp; Quiet Corners
                </h3>
                <p className="mt-1.5 text-[16.5px] leading-7 text-ink/75" data-id="element-674" data-mp="nwu6hsyo8voq">
                  Umbrellas, hammocks and smaller pieces that make the space easier to use
                  throughout the day.
                </p>
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Featured furniture lines */}
      <section id="furniture-lines" aria-labelledby="lines-heading" className="w-full scroll-mt-[120px] border-y border-ink/10 bg-white" data-id="element-675" data-mp="nrcnzm0ibnsm">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-676" data-mp="3r8beoeygeag2">
          <div className="grid grid-cols-1 gap-10 xl:grid-cols-12 xl:gap-12" data-id="element-677" data-mp="z4ijvzmx9n">
            <div className="min-w-0 xl:col-span-9" data-id="element-678" data-mp="3w4a5e97984jn">
              <h2 id="lines-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-679" data-mp="4zjuzrlfu2kw">
                Featured Furniture Lines
              </h2>

              <div className="mt-8 grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-8 lg:gap-10" data-id="element-680" data-mp="38ey0vmmvgkno">
                <div className="min-w-0" data-id="element-681" data-mp="3deapabte5zsd">
                  <div className="aspect-[3/2] w-full overflow-hidden rounded-card border border-ink/10 bg-white" data-id="element-682" data-mp="35exktufpfbgi">
                    <img src="https://www.polywood.com/cdn/shop/files/gv3axo062u1xjb7vi6j0.jpg?format=webp&height=1200&v=1737741334&width=1500" alt="POLYWOOD Lakeside outdoor seating set." className="h-full w-full object-contain p-3 sm:p-4" loading="lazy" decoding="async" data-id="element-683" data-mp="30h4dgy2o0ben" />
                  </div>
                  <div className="mt-5 border-t-2 border-pegasus-magenta pt-5" data-id="element-684" data-mp="2hj1midqk44e7">
                    <h3 className="font-display text-[30px] font-semibold leading-9 tracking-[0.01em] text-ink sm:text-[34px]" data-id="element-685" data-mp="2wfsq6z79k29m">
                      POLYWOOD
                    </h3>
                    <p className="mt-3 text-body leading-7 text-ink/80" data-id="element-686" data-mp="bnqt07ww443c">
                      All-weather furniture made for everyday outdoor use, with seating and dining
                      styles that work around real patios, decks and porches.
                    </p>
                    <a href="https://www.polywood.com/" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-687" data-mp="j81co3el8l92">
                      Visit Manufacturer Site
                      <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-688" data-mp="o7nuyr99wroq" />
                    </a>
                  </div>
                </div>

                <div className="min-w-0" data-id="element-689" data-mp="s6wnxtwholj8">
                  <div className="aspect-[3/2] w-full overflow-hidden rounded-card border border-ink/10 bg-water" data-id="element-690" data-mp="1fyk3jfoawa67">
                    <img src="https://images.prismic.io/winstonfurniture/0q7r9UXt8vXKCUWg_ExteriorLAKEHOUSE_Hermosa2_Hero_3.jpg?auto=format,compress" alt="Winston Hermosa outdoor dining furniture beside a lake." className="h-full w-full object-cover object-[50%_62%]" loading="lazy" decoding="async" data-id="element-691" data-mp="1kpaa2w8gqj2h" />
                  </div>
                  <div className="mt-5 border-t-2 border-pine pt-5" data-id="element-692" data-mp="3f8ofuq31w0bi">
                    <h3 className="font-display text-[30px] font-semibold leading-9 text-ink sm:text-[34px]" data-id="element-693" data-mp="fqouhiuqzcg1">
                      Winston
                    </h3>
                    <p className="mt-3 text-body leading-7 text-ink/80" data-id="element-694" data-mp="60vlmplzpbfh">
                      Outdoor seating and dining collections with a wide range of materials,
                      finishes and silhouettes to compare.
                    </p>
                    <a href="https://www.winstonfurniture.com/" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-695" data-mp="yiziy7onn7um">
                      Visit Manufacturer Site
                      <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-696" data-mp="2vcli5hiq9lvz" />
                    </a>
                  </div>
                </div>
              </div>

              <ul className="mt-10 divide-y divide-ink/15 border-y border-ink/15" data-id="element-697" data-mp="309u1vmjixxzc">
                {secondaryLines.map(line => <li key={line.name} className="flex flex-col gap-1 py-4 sm:flex-row sm:items-baseline sm:gap-6" data-id="element-698" data-mp="393yxskdilzlq">
                    <a href={line.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[32px] shrink-0 items-center gap-1.5 text-[17px] font-semibold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta sm:w-56" data-id="element-699" data-mp="3e0yu43kkbnzn">
                      {line.name}
                      <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-700" data-mp="1nwwz4t8cvtg2" />
                    </a>
                    <span className="text-[16.5px] leading-7 text-ink/75" data-id="element-701" data-mp="21s937r8eiw9n">{line.copy}</span>
                  </li>)}
              </ul>

              <p className="mt-5 max-w-3xl text-[16px] leading-6 text-ink/70" data-id="element-702" data-mp="2bnj76kid1dq2">
                Product lines and showroom availability can change. Ask us for current
                details.
              </p>

              {/* Lead-time notice: in flow below the brands on tablet and mobile */}
              <div className="mt-8 xl:hidden" data-id="element-703" data-mp="cci2nptvhpze">
                <LeadTimeNotice data-id="element-704" data-mp="cdt4vl7o6poe" />
              </div>
            </div>

            {/* Lead-time notice: narrow margin column on wide desktop */}
            <aside aria-label="Special order lead times" className="hidden xl:col-span-3 xl:block" data-id="element-705" data-mp="2sy6ckbjlzu3i">
              <div className="sticky top-[112px]" data-id="element-706" data-mp="2e6xpa936iktj">
                <LeadTimeNotice data-id="element-707" data-mp="2j7bkurnlg2ao" />
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Complete the backyard */}
      <section id="complete-the-backyard" aria-labelledby="complete-heading" className="w-full scroll-mt-[120px] bg-white" data-id="element-708" data-mp="25aez34mc1b0n">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-709" data-mp="1vb7jsbw1f6aw">
          <h2 id="complete-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-710" data-mp="208pm3jfjun9x">
            Complete the Backyard
          </h2>

          <ul className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-10" data-id="element-711" data-mp="3uns30sqj8x18">
            {categories.map(category => <li key={category.title} data-id="element-712" data-mp="2iclq63rxywed">
                <SiteLink to={category.href} className="group block rounded-card focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pegasus-magenta focus-visible:ring-offset-2" data-id="element-713" data-mp="2ifq2xpjh30gn">
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-714" data-mp="23o7mjqmpb3wr">
                    <img src={category.image} alt={category.alt} className="h-full w-full object-cover transition-opacity duration-150 ease-out group-hover:opacity-95" style={{
                  objectPosition: category.position
                }} loading="lazy" decoding="async" data-id="element-715" data-mp="1yq62b2djgyjp" />
                  </div>
                  <h3 className="mt-4 font-display text-[22px] font-semibold leading-8 text-ink group-hover:text-pegasus-blue sm:text-[24px]" data-id="element-716" data-mp="3k2qrjhq3lngm">
                    {category.title}
                  </h3>
                  <p className="mt-1.5 text-[16.5px] leading-7 text-ink/75" data-id="element-717" data-mp="3mn5iia4jtzm3">{category.copy}</p>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-[16px] font-bold text-pegasus-blue group-hover:text-pegasus-magenta" data-id="element-718" data-mp="3rl71kv1z5yxk">
                    Explore
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-719" data-mp="565j5fk3j18m" />
                  </span>
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Showroom close */}
      <section aria-labelledby="showroom-heading" className="w-full border-t border-ink/10 bg-water" data-id="element-720" data-mp="38o5wzbcc20u5">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-721" data-mp="3im3ivs6pzljl">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-722" data-mp="3ik0iqfbusc15">
            <div className="lg:col-span-6" data-id="element-723" data-mp="swi0ti558cce">
              <p className="text-[14px] font-bold uppercase tracking-[0.02em] text-pine" data-id="element-724" data-mp="xvmbyebmok5d">
                Visit Us
              </p>
              <h2 id="showroom-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-[34px] lg:leading-[42px]" data-id="element-725" data-mp="289eckewh92dk">
                See the Materials, Comfort and Scale in Person
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-726" data-mp="2m23ykjc0a7ao">
                Sit in the furniture, compare finishes and get a better sense of how each piece will
                fit your space. Bring approximate measurements or a few photos of your patio, deck
                or yard.
              </p>
              <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-727" data-mp="1yayko9pmlp30">
                We can help you compare the pieces that belong in the conversation.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-728" data-mp="39rf228kqqfsi">
                <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-729" data-mp="39tj6crmjxgtb">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-730" data-mp="3jx6kiyfxiylu" />
                  Location &amp; Hours
                </SiteLink>
                <SiteLink to={routes.contact} className="inline-flex min-h-[48px] items-center rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-731" data-mp="3b3biw0e0nqay">
                  Contact Us
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-6" data-id="element-732" data-mp="2s47bsx61c6lx">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-733" data-mp="2s5idmrfx74ae">
                <img src={images.whyPegasus} alt="The Pegasus Spas storefront in Brooklyn, Michigan with outdoor furniture displayed out front" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-734" data-mp="ud4n0dlkq5ig" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-735" data-mp="2asim0b1vrre3">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-736" data-mp="2eu44cbx6oxvo">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-737" data-mp="32n2ow0u6g0bc">
            {relatedLinks.map(link => <li key={link.label} data-id="element-738" data-mp="37ihzmjww1g3j">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-739" data-mp="2tlw5nk058bee">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-740" data-mp="1enh492bkozuy" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}